<template>
  <div
    :class="{
      'context-menu-fi': true,
      'context-menu-fi--active': isActive,
      'context-menu-fi--align-menu-left': alignContextMenuLeft,
      'context-menu-fi--disabled': disabled,
      'context-menu-fi--menu-full-height': menuFullHeight,
    }"
    data-test="context-menu"
  >
    <div
      ref="toggle"
      :class="{
        'context-menu-fi__toggle': true,
        'context-menu-fi__toggle--custom': $slots.menuToggle,
      }"
      data-test="context-menu-button"
      @click.prevent="toggleClicked"
    >
      <slot
        name="menuToggle"
        :is-open="isActive"
      >
        <icon-fi
          rotate="90"
          icon="more"
        />
      </slot>
    </div>

    <teleport to="body">
      <transition
        enter-active-class="animated fadeIn fastest"
        leave-active-class="animated fadeOut fastest"
      >
        <div
          v-if="isActive"
          ref="flyoutContainer"
          :class="{
            'context-menu-fi__flyout-container': true,
            'context-menu-fi__flyout-container--mobile-full-screen': mobileFullScreen && !isSM,
          }"
          :style="popupStyle"
        >
          <div
            v-click-outside="close"
            class="context-menu-fi__flyout"
          >
            <div
              v-if="loading"
              class="fi:h-full fi:bg-grey fi:sm:min-h-24"
            >
              <loading-indicator
                :variant="isSM ? 'spinnerLocal' : 'spinnerFullPage'"
                :spinoff="isSM ? 'in-relative-centered' : undefined"
              />
            </div>
            <template v-else-if="items?.length">
              <div
                v-if="!isSM"
                class="context-menu-fi__flyout-item context-menu-fi__flyout-item--close"
                @click="close"
              >
                <slot name="mobileItemsTitle">
                  {{ mobileItemsTitle }}
                </slot>
                <icon-fi icon="close" />
              </div>
              <div
                v-for="(item, index) in items"
                :key="`${item.title}-${index}`"
                :class="{
                  'context-menu-fi__flyout-item': true,
                  'context-menu-fi__flyout-item--selected': item.selected,
                  'context-menu-fi__flyout-item--margin': item.margin,
                  'context-menu-fi__flyout-item--no-lg-flyout-padding': noLgFlyoutPadding,
                }"
                data-test="context-menu-item"
                data-test-3="item.value"
                @click="onClick(item)"
              >
                <slot
                  name="item"
                  :item="item"
                >
                  {{ item.title }}
                </slot>
              </div>
            </template>
            <template v-else-if="$slots.menuItems">
              <slot name="menuItems" />
            </template>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup lang="ts" generic="T extends ContextMenuItem">
import { computed, ref, useTemplateRef, watch, watchEffect } from 'vue';

import IconFi from '@/components/atoms/icon-fi/IconFi.vue';
import LoadingIndicator from '@/components/atoms/loading-indicator/LoadingIndicator.vue';
import { useBodyWidth } from '@/composables/bodyWidth';
import { useBreakpoints } from '@/composables/breakpoint';

export interface ContextMenuItem {
  action: string;
  title: string;
  selected?: boolean;
  margin?: boolean;
}

defineOptions({
  name: 'ContextMenuFi',
});

const props = withDefaults(
  defineProps<{
    mobileItemsTitle?: string;
    items?: T[];
    stayOpen?: boolean;
    alignContextMenuLeft?: boolean;
    disabled?: boolean;
    loading?: boolean;
    menuFullHeight?: boolean;
    mobileFullScreen?: boolean;
    flyoutWidth?: number | undefined;
    noLgFlyoutPadding?: boolean;
    openToLeft?: boolean;
  }>(),
  {
    mobileItemsTitle: '',
    items: () => [],
    flyoutWidth: undefined,
  },
);

const emit = defineEmits<{
  'context-menu-action': [action: string, item: T];
  close: [value: void];
  open: [value: void];
}>();

const { isSM, windowDimensions } = useBreakpoints();
const { bodyWidth } = useBodyWidth();

const toggle = useTemplateRef<HTMLDivElement>('toggle');
const flyoutContainer = useTemplateRef<HTMLDivElement>('flyoutContainer');
const isActive = ref(false);

watch(isActive, (value) => {
  if (value) {
    emit('open');
  } else {
    emit('close');
  }
});

const close = () => {
  isActive.value = false;
};

const toggleClicked = () => {
  if (props.disabled) {
    return;
  }

  isActive.value = !isActive.value;
};

const onClick = (item: T) => {
  if (item && !item.action) {
    return;
  }

  emit('context-menu-action', item.action, item);

  if (!props.stayOpen) {
    close();
  }
};

const checkIsFixed = (element: HTMLElement | null) => {
  while (element) {
    if (window.getComputedStyle(element).position === 'fixed') {
      return true;
    }
    element = element.parentElement;
  }
  return false;
};

const popupStyle = ref<undefined | Record<string, string>>();
watchEffect(() => {
  if (!isActive.value || !toggle.value) {
    popupStyle.value = {
      display: 'none',
    };
    return;
  }

  const flyoutRect = flyoutContainer.value?.getBoundingClientRect();
  const rect = toggle.value.getBoundingClientRect();

  const isFixed = checkIsFixed(toggle.value);
  const scrollYValue = isFixed ? 0 : window.scrollY;
  const scrollXValue = isFixed ? 0 : window.scrollX;

  const rectLeft = Math.floor(rect.left + scrollXValue);
  const rectRight = Math.floor(
    (document?.body?.clientWidth || windowDimensions.value.width) - rect.right,
  );
  const maxLeft = bodyWidth.value - (flyoutRect?.width || 0);
  const left = Math.min(rectLeft, maxLeft);

  const rectHeight = Math.floor(rect.bottom + scrollYValue);
  const maxHeight = scrollYValue + windowDimensions.value.height - (flyoutRect?.height || 0);
  const top = Math.min(rectHeight, maxHeight);

  const position = isFixed ? 'fixed' : 'absolute';

  const positionX: any = computed(() => {
    if (props.openToLeft && isSM.value) {
      return { left: 'auto', right: rectRight + 'px' };
    }
    return { left: left + 'px' };
  });

  popupStyle.value = {
    top: `${top}px`,
    position,
    ...positionX.value,
    ...(props.flyoutWidth
      ? {
          width: `${props.flyoutWidth}px`,
          minWidth: `${props.flyoutWidth}px`,
        }
      : {}),
  };
});

defineExpose({
  close,
});
</script>

<style src="./context-menu-fi-unscoped" />

<style scoped src="./context-menu-fi" />
